import { RouteAuthenticationRequirement, RouteItem } from '@cks-internal/react-base';
import { Navigate, useLocation } from 'react-router-dom';
import { AppRoles } from '../constants/appRoles';
import { Layout } from '../components/layout/layout.tsx';
import { ApplicationRoutes } from '../constants/appRoutes';
import {
  createFlaggedLegacyRoute,
  createForcedLegacyLeaf,
  createForcedLegacyRoute,
  createLegacyComponent,
  createLegacyLeaf,
  createLegacyRoute
} from './legacyRouteHelpers.tsx';
import { FeatureFlags } from '../constants/featureFlags.ts';

// --- Shared role arrays used across categories ---
const timesheetRoles = [
  AppRoles.AdminJr,
  AppRoles.Admin,
  AppRoles.Checker,
  AppRoles.Drafter,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.PartyChief,
  AppRoles.SurveyAssistant
];
const managementRoles = [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.QuickLinkManagement];
const adminOnly = [AppRoles.Admin];
const reportViewerRoles = [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager];
const municipalReportingRoles = [...reportViewerRoles, AppRoles.MunicipalOfficeReporting];
const companyCrmRoles = [
  AppRoles.Admin,
  AppRoles.AdminJr,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.QuickLinkManagement
];
const fieldTimeApprovalRoles = [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.FieldTimeApprover];
const officeTimeApprovalRoles = [AppRoles.Admin, AppRoles.OfficeTimeApprover, AppRoles.SupervisorOfficeTimeApprover];
const invoiceApprovalRoles = [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr];
const userManagementRoles = [AppRoles.Admin];
const editJobRoles = [
  AppRoles.AdminJr,
  AppRoles.Admin,
  AppRoles.Checker,
  AppRoles.Drafter,
  AppRoles.ProjectManager,
  AppRoles.Manager,
  AppRoles.PartyChief,
  AppRoles.SurveyAssistant,
  AppRoles.VendorAdmin
];

const LegacyEditJobRedirect = () => {
  const location = useLocation();

  return <Navigate to={`/legacy/editjob${location.search}${location.hash}`} replace />;
};

const quickLinksRouteItem: RouteItem = {
  text: 'Quick Links',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.quickLinks.overview,
  allowedRoles: [],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/quickLinks/QuickLinksPage.tsx');
    return { Component: component.default };
  }
};

const quickLinkManagementRouteItem: RouteItem = {
  text: 'Quick Link Management',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.quickLinks.management,
  allowedRoles: [AppRoles.Admin, AppRoles.QuickLinkManagement],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/v2/quickLinks/QuickLinkManagementPage.tsx');
    return { Component: component.default };
  }
};

const hrManagementRouteItem: RouteItem = {
  text: 'HR',
  authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
  path: ApplicationRoutes.admin.hrManagement,
  allowedRoles: [],
  layout: <Layout />,
  async lazy() {
    const component = await import('../features/hrManagement/overview/HrManagement.tsx');
    return { Component: component.default };
  }
};

/**
 * Main navigation items in the requested order:
 * Job Book, Timesheet, Purchases, Management, Inventory, Safety, Payroll, Invoicing, Field Tools, Quick Links, Admin.
 */
export function getLegacyNavItems(isHr: boolean): RouteItem[] {
  return [
    // 1. Job Book (React tabbed workspace when `NewJobBook` flag is enabled,
    //    legacy iframe fallback otherwise)
    createFlaggedLegacyRoute({
      text: 'Job Book',
      path: ApplicationRoutes.jobs.overview,
      legacyPath: '/JobBook/Index',
      allowedRoles: [
        AppRoles.AdminJr,
        AppRoles.Admin,
        AppRoles.Checker,
        AppRoles.Drafter,
        AppRoles.ProjectManager,
        AppRoles.Manager,
        AppRoles.PartyChief,
        AppRoles.SurveyAssistant,
        AppRoles.VendorAdmin
      ],
      flagKey: FeatureFlags.NewJobBook,
      loadNewComponent: () => import('../features/v2/jobs/overview/JobBookManagement.tsx')
    }),

    // 2. Timesheet (dropdown) – user list first, then leftovers at bottom
    {
      text: 'Timesheet',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: timesheetRoles,
      layout: <Layout />,
      subItems: [
        createLegacyLeaf('My Time Off Requests', '/legacy/time-off', '/TimeOffRequests/Index', timesheetRoles, true),
        createLegacyLeaf(
          'My Safety Report Tickets',
          '/legacy/timesheet/safety',
          '/DailySafetyTickets/Index',
          [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        createLegacyLeaf(
          'Request Bank Time Payout',
          '/legacy/time-off/request-bank-time-payout',
          '/BankTime/RequestBankTimePayout',
          [AppRoles.BankedTime],
          true
        ),
        // Leftovers at bottom
        createLegacyLeaf('Timesheet', '/legacy/timesheet', '/TimesheetNew/Index', timesheetRoles, true),
        createLegacyLeaf(
          'Monthly Timesheet Invoice',
          '/legacy/timesheet/invoice',
          '/TimesheetNew/TimesheetInvoice',
          [AppRoles.Admin, AppRoles.PartyChief],
          true
        )
      ]
    },

    // 4. Purchases
    {
      text: 'Purchases',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.AdminJr,
        AppRoles.Admin,
        AppRoles.PartyChief,
        AppRoles.PurchaseOrderOnly,
        AppRoles.Manager
      ],
      layout: <Layout />,
      subItems: [
        {
          text: 'Purchase Orders',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.purchasing.purchaseOrders,
          allowedRoles: [
            AppRoles.AdminJr,
            AppRoles.Admin,
            AppRoles.PartyChief,
            AppRoles.PurchaseOrderOnly,
            AppRoles.Manager
          ],
          async lazy() {
            const component = await import('../features/v2/purchasing/purchaseOrderReport/PurchaseOrderReportPage.tsx');
            return { Component: component.default };
          }
        },
        createLegacyLeaf(
          'Vendor Purchases',
          '/legacy/accounting/vendor-purchases',
          '/Accounting/VendorPurchases',
          [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.AdminJr],
          true
        ),
        createLegacyLeaf(
          'Accommodations Report',
          '/legacy/reporting/accommodations',
          '/Reporting/AccomodationsReport',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 5. Management
    {
      text: 'Management',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: managementRoles,
      layout: <Layout />,
      subItems: [
        {
          text: 'Company CRM',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.companyManagement,
          allowedRoles: companyCrmRoles,
          async lazy() {
            const component = await import('../features/v2/company/overview/CompanyManagement.tsx');
            return { Component: component.default };
          }
        },
        createLegacyLeaf(
          'Crew Tracking',
          '/legacy/management/crew-tracking',
          '/CrewTracking/Index',
          [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
          true
        ),
        createLegacyLeaf(
          'Job Status Report',
          '/legacy/management/job-status',
          '/JobStatusReport/Index',
          [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
          true
        ),
        quickLinkManagementRouteItem,
        createLegacyLeaf(
          'Custom Job Code Management',
          '/legacy/admin/custom-job-codes',
          '/CustomJobManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Custom Job Management',
          '/legacy/admin/custom-jobs',
          '/CustomJobManagement/JobManagement',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'DropDown/Application Management',
          '/legacy/admin/dropdowns',
          '/Admin/DropDown/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf('Job Locks', '/legacy/admin/job-locks', '/Admin/EditLock/Index', adminOnly, true),
        createLegacyLeaf(
          'Mailing List Management',
          '/legacy/admin/mailing-lists',
          '/Admin/MailListManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Email Group Management',
          '/legacy/admin/email-groups',
          '/Admin/EmailGroups/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Report Builder Management',
          '/legacy/admin/report-builder',
          '/ReportBuilder/Manage',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        ),
        createLegacyLeaf(
          'Job Report Builder',
          '/legacy/reporting/report-builder',
          '/ReportBuilder/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Production Status Report',
          '/legacy/reporting/job-production',
          '/ProductionStatus/Index',
          [AppRoles.Admin, AppRoles.ProductionStatusReport],
          true
        ),
        createLegacyLeaf(
          'Legal Status Report',
          '/legacy/reporting/legal-status',
          '/ProductionStatus/LegalStatusReport',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Municipal Field Work Listings',
          '/legacy/reporting/municipal-field-work',
          '/Reporting/MunicipalJobsReport?field=true&misc=false',
          municipalReportingRoles,
          true
        ),
        createLegacyLeaf(
          'Municipal Misc Office Report',
          '/legacy/reporting/municipal-misc-office',
          '/Reporting/MunicipalJobsReport?field=false&misc=true',
          municipalReportingRoles,
          true
        )
      ]
    },

    ...(isHr ? [hrManagementRouteItem] : []),

    // 6. Inventory
    {
      text: 'Inventory',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.Admin,
        AppRoles.AdminJr,
        AppRoles.AccountingAdmin,
        AppRoles.OfficeEquipmentManagement,
        AppRoles.Manager
      ],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Field Equipment Management',
          '/legacy/admin/field-equipment',
          '/Equipment/Index?isOfficeEquipment=false',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Office Equipment Management',
          '/legacy/admin/office-equipment',
          '/Equipment/Index?isOfficeEquipment=true',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin, AppRoles.OfficeEquipmentManagement],
          true
        ),
        {
          text: 'Equipment History Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.equipmentHistoryReport,
          allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin],
          async lazy() {
            const m = await import('../features/v2/equipment/history/EquipmentHistoryReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'Equipment Owner Report',
          '/legacy/reporting/equipment-owner',
          '/Equipment/EquipmentOwnerReport?isFieldEquipment=true',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 7. Safety
    {
      text: 'Safety',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager, AppRoles.SafetyManagement],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Safety Ticket Management',
          '/legacy/admin/safety-tickets',
          '/Admin/DropDown/SafetyTicketManagement',
          [AppRoles.Admin, AppRoles.ProjectManager, AppRoles.Manager],
          true
        ),
        createLegacyLeaf(
          'Daily Safety Report Tickets',
          '/legacy/reporting/daily-safety',
          '/DailySafetyTickets/Index',
          [AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        createLegacyLeaf(
          'Job KM Usage Report',
          '/legacy/reporting/job-km',
          '/EquipmentUsageReport/JobKmReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'My Safety Report Tickets',
          '/legacy/timesheet/safety',
          '/DailySafetyTickets/Index',
          [AppRoles.PartyChief, AppRoles.Admin, AppRoles.SafetyManagement],
          true
        ),
        {
          text: 'Safety Ticket Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.safety.safetyTicketReport,
          allowedRoles: reportViewerRoles,
          async lazy() {
            const m = await import('../features/v2/safety/safetyTicketReport/SafetyTicketReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'ISNetworld Safety Report',
          '/legacy/reporting/isnetworld',
          '/Reporting/IsNetworldSafetyReport',
          [AppRoles.Admin, AppRoles.AdminJr, AppRoles.Manager],
          true
        )
      ]
    },

    // 8. Payroll
    {
      text: 'Payroll',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [
        AppRoles.Admin,
        AppRoles.AdminJr,
        AppRoles.Manager,
        AppRoles.ProjectManager,
        AppRoles.OfficeTimeApprover,
        AppRoles.FieldTimeApprover
      ],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Field Time Approvals',
          '/legacy/admin/field-time-approvals',
          '/TimesheetApproval/Index?isOfficeTime=false',
          fieldTimeApprovalRoles,
          true
        ),
        createLegacyLeaf(
          'Office Time Approvals',
          '/legacy/admin/office-time-approvals',
          '/TimesheetApproval/Index?isOfficeTime=true',
          officeTimeApprovalRoles,
          true
        ),
        createLegacyLeaf(
          'Time Off Approvals',
          '/legacy/admin/time-off-approvals',
          '/TimeOffApproval/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf('Stat Holidays', '/legacy/admin/holidays', '/Admin/HolidayManagement/Index', adminOnly, true),
        createLegacyLeaf(
          'Biweekly Payroll Report',
          '/legacy/reporting/biweekly-payroll',
          '/Reporting/PayrollReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Monthly Payroll Report',
          '/legacy/reporting/monthly-payroll',
          '/Reporting/MonthlyPayrollReport',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Production Payroll Report',
          '/legacy/reporting/production-payroll',
          '/TimesheetNew/ProductionPayrollReport',
          adminOnly,
          true
        ),
        {
          text: 'Timesheet Summary',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.reporting.timesheetSummary,
          allowedRoles: reportViewerRoles,
          async lazy() {
            const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
            return { Component: m.default };
          }
        },
        {
          text: 'Timesheet Summary (Field)',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.reporting.timesheetSummaryField,
          allowedRoles: managementRoles,
          async lazy() {
            const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
            const TimesheetSummaryReportPage = m.default;
            return { Component: () => <TimesheetSummaryReportPage fieldTimeOnly={true} /> };
          }
        },
        createLegacyLeaf(
          'Equipment Usage Report',
          '/legacy/reporting/equipment-usage',
          '/EquipmentUsageReport/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Equipment Usage Charge Rates',
          '/legacy/accounting/equipment-rates',
          '/EquipmentRates/EquipmentUsageChargeRates',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Employee Report',
          '/legacy/reporting/user-report',
          '/Admin/UserManagement/UserManagementReport',
          reportViewerRoles,
          true
        )
      ]
    },

    // 9. Invoicing
    {
      text: 'Invoicing',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin, AppRoles.AdminJr],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Account Code Management',
          '/legacy/accounting/codes',
          '/InvoiceCodeManagement/Index',
          [AppRoles.AccountingAdmin],
          true
        ),
        createForcedLegacyLeaf(
          'Approved Invoice Report',
          '/legacy/accounting/approved-invoices',
          '/InvoiceReport/ApprovedInvoiceReport',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Change Compass Address',
          '/legacy/accounting/address',
          '/CompassAddress/Index',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Invoice Report',
          '/legacy/reporting/invoice-report',
          '/InvoiceReport/Index',
          reportViewerRoles,
          true
        ),
        createLegacyLeaf(
          'Daily Field Ratesheet Management',
          '/legacy/admin/daily-field-rates',
          '/DailyFieldRateManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Job Ratesheet Management',
          '/legacy/admin/job-ratesheets',
          '/Admin/RateSheetManagement/Index',
          adminOnly,
          true
        ),
        createLegacyLeaf(
          'Invoice Rules Engine',
          '/legacy/admin/invoice-rules',
          '/InvoiceRulesEngine/Index',
          adminOnly,
          true
        ),
        createFlaggedLegacyRoute({
          text: 'Invoice Approval',
          path: ApplicationRoutes.invoicing.invoiceApproval,
          legacyPath: '/Accounting/Invoices',
          allowedRoles: invoiceApprovalRoles,
          flagKey: FeatureFlags.UseLegacyInvoiceApproval,
          loadNewComponent: () => import('../features/v2/invoicing/invoiceApproval/InvoiceApprovalPage.tsx'),
          isNewApp: true,
          serveLegacyWhenEnabled: true
        }),
        {
          text: 'Invoice Number Report',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.invoicing.invoiceNumberReport,
          allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin],
          async lazy() {
            const m = await import('../features/v2/invoicing/invoiceNumberReport/InvoiceNumberReportPage.tsx');
            return { Component: m.default };
          }
        },
        createLegacyLeaf(
          'Charge Out Ratio Report',
          '/legacy/accounting/charge-out',
          '/AdminReport/ChargeOutRatioReport',
          [AppRoles.AccountingAdmin],
          true
        ),
        createLegacyLeaf(
          'Sales Report',
          '/legacy/accounting/sales',
          '/Reporting/SalesReport',
          [AppRoles.Admin, AppRoles.AccountingAdmin],
          true
        )
      ]
    },

    // 10. Field Tools – user list first, then leftovers at bottom
    {
      text: 'Field Tools',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief, AppRoles.Gis],
      layout: <Layout />,
      subItems: [
        createLegacyLeaf(
          'Survey Points',
          '/legacy/survey-points',
          '/SurveyPoint/Index',
          [AppRoles.Admin, AppRoles.Manager, AppRoles.PartyChief],
          true
        ),
        createLegacyLeaf(
          'Survey Point Filter Management',
          '/legacy/admin/survey-filters',
          '/Admin/SurveyPointFilterManagement/Index',
          adminOnly,
          true
        ),
        // Leftovers at bottom
        createLegacyLeaf('Field Data', '/legacy/field-data', '/FieldData/Index', [], true)
      ]
    },

    // 11. Quick Links
    quickLinksRouteItem,

    {
      text: 'Location Converter',
      authenticationRequirement: RouteAuthenticationRequirement.Either,
      path: '/legacy/location-converter',
      layout: <Layout />,
      lazy: createLegacyComponent('/Tools/LocationConvertor', true)
    },

    // 12. Admin – leftovers (not in user's main categories)
    {
      text: 'Admin',
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: [AppRoles.Admin],
      layout: <Layout />,
      subItems: [
        {
          text: 'User Management',
          authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
          path: ApplicationRoutes.admin.userManagement,
          allowedRoles: userManagementRoles,
          async lazy() {
            const component = await import('../features/userAdmin/UserManagement.tsx');
            return { Component: component.default };
          }
        }
      ]
    }
  ];
}

export const TimesheetLockedOutNavItems: RouteItem[] = [
  createLegacyRoute(
    'Timesheet',
    '/legacy/timesheet-locked-out',
    '/TimesheetNew/Index',
    [AppRoles.TimesheetLockedOut],
    undefined,
    true
  ),
  createLegacyRoute('Field Data', '/legacy/field-data', '/FieldData/Index', [], undefined, true),
  quickLinksRouteItem,
  {
    text: 'Location Converter',
    authenticationRequirement: RouteAuthenticationRequirement.Either,
    path: '/legacy/location-converter',
    layout: <Layout />,
    lazy: createLegacyComponent('/Tools/LocationConvertor', true)
  }
];

// Non-nav routes (accessible via links but not shown in navigation)
export const LegacyHiddenRoutes: RouteItem[] = [
  {
    text: 'Timesheet Summary (Admin Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.reporting.timesheetSummaryAdmin,
    allowedRoles: [AppRoles.AdminJr],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/reporting/timesheetSummary/TimesheetSummaryReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Invoice Number Report (Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/accounting/invoice-numbers',
    allowedRoles: [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.VendorAdmin],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/invoicing/invoiceNumberReport/InvoiceNumberReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Safety Ticket Report (Legacy Path)',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/reporting/safety-ticket-report',
    allowedRoles: reportViewerRoles,
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/safety/safetyTicketReport/SafetyTicketReportPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Equipment History Detail',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: ApplicationRoutes.admin.equipmentHistoryReportDetail,
    allowedRoles: [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin],
    layout: <Layout />,
    async lazy() {
      const m = await import('../features/v2/equipment/history/EquipmentHistoryDetailPage.tsx');
      return { Component: m.default };
    }
  },
  {
    text: 'Edit Job Legacy Redirect',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/EditJob',
    additionalPaths: ['/EditJob/Index'],
    allowedRoles: editJobRoles,
    layout: <Layout />,
    async lazy() {
      return { Component: LegacyEditJobRedirect };
    }
  },
  createLegacyRoute('Edit Job', '/legacy/editjob', '/EditJob', editJobRoles, undefined, true),
  createLegacyRoute(
    'Admin Vendor Purchases',
    '/legacy/admin/vendor-purchases',
    '/Accounting/VendorPurchases',
    [AppRoles.AdminJr],
    undefined,
    true
  ),
  createLegacyRoute(
    'Equipment Direct Entry',
    '/Equipment/Index',
    '/Equipment/Index',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.AccountingAdmin, AppRoles.OfficeEquipmentManagement],
    undefined,
    true
  ),
  createLegacyRoute(
    'Edit Job Legal',
    '/legacy/edit-job/legal',
    '/EditJob/Legal',
    [AppRoles.AdminJr, AppRoles.Admin, AppRoles.Manager],
    undefined,
    true
  ),
  createLegacyRoute(
    'Job Costing',
    '/legacy/costing',
    '/Costing/Index',
    [AppRoles.Admin, AppRoles.AccountingAdmin, AppRoles.ProjectManager, AppRoles.Manager],
    undefined,
    true
  ),
  createLegacyRoute(
    'Job Activity Report',
    '/legacy/reporting/job-activity',
    '/Reporting/JobActivityReport',
    [
      AppRoles.AdminJr,
      AppRoles.Admin,
      AppRoles.Checker,
      AppRoles.Drafter,
      AppRoles.ProjectManager,
      AppRoles.Manager,
      AppRoles.PartyChief,
      AppRoles.SurveyAssistant,
      AppRoles.VendorAdmin
    ],
    undefined,
    true
  ),
  createForcedLegacyRoute(
    'Job Invoice',
    '/legacy/invoicing/job-invoice',
    '/Invoicing/JobInvoice',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.PartyChief],
    undefined,
    true
  ),
  {
    text: 'Daily Field Rate',
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: '/legacy/invoicing/daily-field-rate',
    allowedRoles: [AppRoles.Admin, AppRoles.PartyChief],
    layout: <Layout />,
    lazy: async () => {
      const { DailyFieldRateRoute } = await import('../components/invoicing/DailyFieldRateRoute');
      return { Component: DailyFieldRateRoute };
    }
  },
  createForcedLegacyRoute(
    'View Job Invoices',
    '/legacy/invoicing/view-invoices',
    '/Invoicing/ViewJobInvoices',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager],
    undefined,
    true
  ),
  createForcedLegacyRoute(
    'Print Invoice',
    '/legacy/invoicing/print',
    '/Invoicing/PrintJobInvoice',
    [AppRoles.Admin, AppRoles.AdminJr, AppRoles.ProjectManager, AppRoles.Manager, AppRoles.AccountingAdmin],
    undefined,
    true
  ),
  createLegacyRoute('Job History', '/legacy/job-history', '/JobHistory/Index', [AppRoles.Admin], undefined, true)
];
